import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  HeaderComponent,
  HeaderIconClickEvent,
  HeaderIconConfig,
  SessionTimeoutComponent,
  SpinnerComponent,
  WorkspaceManagementComponent,
  WorkspaceManagementSettingsChangeEvent
} from 'fi-ui-common';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { Menubar } from 'primeng/menubar';
import { Toast } from 'primeng/toast';
import { User, UserService } from './services/user-service';
import { Popover } from 'primeng/popover';
import { filter, Observable, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { UserPreferences } from './shared/components/user-preferences/user-preferences';
import { select, Store } from '@ngxs/store';
import {
  CreateLayout,
  FetchViews,
  ResetLayoutChanges,
  ResizeGridster,
  SaveLayout,
  SetSelectedLayout,
  UpdateAutosaveSettings
} from './shared/stores/workspace/workspace-management.actions';
import { WorkspaceManagementSelectors } from './shared/stores/workspace/workspace-management.selectors';
import { Tooltip } from 'primeng/tooltip';
import { AsyncPipe } from '@angular/common';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    SpinnerComponent,
    Menubar,
    Toast,
    Popover,
    FormsModule,
    UserPreferences,
    Tooltip,
    WorkspaceManagementComponent,
    AsyncPipe,
    SessionTimeoutComponent,
    ConfirmDialog
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private userService = inject(UserService);
  private router = inject(Router);
  private store = inject(Store);

  settingsList = select(WorkspaceManagementSelectors.getSettingsList);
  selectedLayout = select(WorkspaceManagementSelectors.getSelectedLayout);
  views = select(WorkspaceManagementSelectors.getViews);
  hasUnsavedChanges = computed(() => {
    const layout = this.selectedLayout();
    const savedViews = layout?.configuration?.views;
    if (!savedViews) return false;

    const currentViews = this.views().filter(view => view.component !== 'TranchesCompleted');

    return savedViews.some(saved => {
      const matchingView = currentViews.find(current => current.label === saved.label);
      if (!saved.selected && !matchingView?.selected) return false;
      return JSON.stringify(matchingView) !== JSON.stringify(saved);
    });
  });

  private userInfoPopover = viewChild<Popover>('userInfoPopover');
  protected readonly title = signal('Global Syndicate Pricer');
  items: MenuItem[] | undefined;
  user: Observable<User>;
  userTeam = signal('');
  userIcon: HeaderIconConfig[] = [];
  isWorkspaceManagementVisible = signal(false);
  loggedIn = signal(false);

  ngOnInit(): void {
    this.user = this.userService.user.pipe(
      filter(user => !!user.name), // Ensure we have a valid user before proceeding
      tap(user => {
        this.loggedIn.set(true);
        this.items = [
          { label: 'Home', routerLink: '/dashboard' },
          { label: 'Issuer', routerLink: '/issuer' },
          { label: 'History', routerLink: '/history' },
          { label: 'Reports', routerLink: '/reports' },
          { label: 'Admin', routerLink: '/admin', visible: user.isAdmin || user.isReadOnly },
          { label: 'Archived', routerLink: '/archived' },
        ];
        this.userIcon.push({
          icon: PrimeIcons.USER,
          tooltipMsg: 'User Info',
          callback: (e: HeaderIconClickEvent) => this.showUserInfo(e),
          styles: {
            'font-size': '18px',
            color: 'white'
          }
        })
      }),
    );
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      tap(event => this.isWorkspaceManagementVisible.set(event.url === '/issuer'))
    ).subscribe()
  }

  showUserInfo(e: HeaderIconClickEvent): void {
    this.userInfoPopover().toggle(e.clickEvent);
  }

  createNewConfig(e: any): void {
    this.store.dispatch(new CreateLayout(e.name, e.layoutConfig || null));
  }

  onLayoutChange(event: any): void {
    this.store.dispatch([new SetSelectedLayout(event), new FetchViews()]);
    // SetTimeout is used to fix Gridster behavior and ensure that the layout has been updated fully. 
    // Resize the gridster after a short delay to ensure the layout has been updated and feats to a container size.
    setTimeout(() => this.store.dispatch([new SetSelectedLayout(event), new FetchViews(), new ResizeGridster()]));
  }

  saveLayout(): void {
    this.store.dispatch(new SaveLayout('auto', true));
  }

  resetLayoutChanges(): void {
    this.store.dispatch(new ResetLayoutChanges());
    // SetTimeout is used to fix Gridster behavior and ensure that the layout has been updated fully. 
    // Resize the gridster after a short delay to ensure the layout has been updated and feats to a container size.
    setTimeout(() => this.store.dispatch(this.store.dispatch([new ResetLayoutChanges(), new ResizeGridster()])));
  }

  onSettingsChange(event: WorkspaceManagementSettingsChangeEvent): void {
    const settingsUpdate = {};
    event.settingsList.forEach(group => {
      group.settings.forEach(setting => {
        settingsUpdate[setting.field] = setting.value;
      });
    });
    this.store.dispatch(new UpdateAutosaveSettings(settingsUpdate));
  }
}
